// ** React Imports
import { useEffect, useContext, useState, useRef, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getUserProfileFollowing } from "store/modules/user-profile/actions/get-user-profile-following.action";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Components
import LoadingContainer from "components/layout/LoadingContainer";
import Loader, { StyledLoaderWrapperFullWidth } from "components/layout/Loader";
import SimpleUsersGrid from "components/layout/UsersGrid/SimpleUsersGrid";
import SelectorSelectSingle from "components/layout/DropdownSelectors/SelectorSelectSingle";
import Block from "components/layout/Block";

// ** Custom Hooks
import useFilterSexV2 from "hooks/filters/useFilterSex_V2";

// ** Utils & Helpers
import generatePath from "utils/generatePath";
import { ANY } from "helpers/constants";

// ** Contexts
import authContext from "contexts/authContext";

// #####################################################

const Following = ({
	match: {
		params: { page: pageUrl, sex: filterSexUrl },
	},
	route: { path },
	history: { push },
}) => {
	const user = useSelector((state) => state.userProfileMain);
	const isAuth = useContext(authContext);
	const { t } = useTranslation(["userProfile"]);

	const location = useLocation();
	const history = useHistory();

	const dispatch = useDispatch();

	const following = useSelector(
		(state) => state.userProfileFriends.following
	);

	const [isChangingFilter, setIsChangingFilter] = useState(false);

	const currentPage = Number(pageUrl || 1);

	const {
		items: filterSexItems,
		selected: filterSexSelected,
		setSelected: filterSexSetSelected,
		compute: filterSexCompute,
	} = useFilterSexV2({ initial: filterSexUrl || ANY, multiple: false });

	const containerRef = useRef(null);
	const triggerObserver = useRef();
	const triggerRef = useCallback(
		(node) => {
			if (following.loading || !("IntersectionObserver" in window))
				return;
			if (
				following.pagination.currentPage >=
				following.pagination.totalPages
			)
				return null;
			if (triggerObserver.current) triggerObserver.current.disconnect();
			const marginValue =
				containerRef.current?.getBoundingClientRect().height * 0.15 ||
				600;
			triggerObserver.current = new IntersectionObserver(
				(entries) => {
					const isIntersecting = entries.some(
						(entry) => entry.isIntersecting
					);
					if (isIntersecting) {
						dispatch(
							getUserProfileFollowing({
								userId: user.id,
								isAuth,
								page: following.pagination.currentPage + 1,
								sex: filterSexCompute.valueToUrl(
									filterSexSelected
								),
								infinityScroll: true,
							})
						);
					}
				},
				{ rootMargin: `0px 0px ${marginValue}px 0px` }
			);
			if (node) triggerObserver.current.observe(node);
		},
		[following.loading]
	);

	useEffect(() => {
		if (user.loaded && !location.state?.forceReload) {
			setIsChangingFilter(true);
			dispatch(
				getUserProfileFollowing({
					userId: user.id,
					isAuth,
					page: currentPage,
					sex: filterSexCompute.valueToUrl(filterSexSelected),
				})
			).finally(() => {
				setIsChangingFilter(false);
			});
		}
	}, [user.id, filterSexSelected]);

	useEffect(() => {
		if (user.loaded && location.state?.forceReload) {
			setIsChangingFilter(true);
			dispatch(
				getUserProfileFollowing({
					userId: user.id,
					isAuth,
					page: 1,
					sex: "any",
				})
			).finally(() => {
				setIsChangingFilter(false);
			});

			history.replace({ ...location, state: null });
		}
	}, [location.state?.forceReload]);

	const renderFilterSex = () => (
		<SelectorSelectSingle
			label={t("common:gender")}
			emptyText={t("common:any.2")}
			items={filterSexItems}
			selected={filterSexSelected}
			onAccept={(value) => {
				filterSexSetSelected(value);
				const nextUrl = generatePath(path, {
					login: user.login?.toLowerCase(),
					sex: filterSexCompute.valueToUrl(value),
					page: 1,
				});
				push(nextUrl);
			}}
			withMobileVersion
		/>
	);

	const showFilterSex = user.statistics.userFollowing > 0;

	// #####################################################

	return (
		<>
			{showFilterSex && (
				<Block
					pb={
						following.loaded && following.list?.length === 0 ? 2 : 0
					}
					width="200px"
				>
					{renderFilterSex()}
				</Block>
			)}
			{!following.loading && following.loaded && (
				<LoadingContainer
					loading={following.loading}
					empty={following.loaded && following.list?.length === 0}
					noContentText={
						following.isPrivate
							? t("userProfile:privateFollowing")
							: t("userProfile:noFollowing")
					}
					noContentOnCriteriaText={
						following.isPrivate
							? t("userProfile:privateFollowing")
							: t("userProfile:noFollowing")
					}
					noCenter
					noMinWidth
					noMinHeight
					marginTopNoCenter={2}
					ref={containerRef}
				/>
			)}
			{!isChangingFilter && following.list?.length > 0 ? (
				<SimpleUsersGrid items={following.list} />
			) : null}
			{(following.loading || isChangingFilter) && (
				<StyledLoaderWrapperFullWidth marginProfil>
					<Loader />
				</StyledLoaderWrapperFullWidth>
			)}
			{!following.loading &&
				!isChangingFilter &&
				!(
					following.pagination?.currentPage > 0 &&
					following.list?.length === 0
				) && (
					<div
						ref={triggerRef}
						style={{
							height: "1px",
							pointerEvents: "none",
						}}
					/>
				)}
		</>
	);
};

// #####################################################

export default Following;

// #####################################################
